/**
 * Who is using which machine: one row per machine with its room (heavy-work slots where it reports
 * its load, else sessions; ADR 0063 step 5), users, labels, limit and drain.
 */
import { CirclePause, CirclePlay, Server } from '@orgus/ui/icons';
import { Link, useNavigate } from 'react-router';
import { sessionCap } from '@orgus/protocol';
import type { Id, Machine, NodeCapacity, Project, SchedulerState } from '@orgus/protocol';
import { Button, EmptyState, MeterBar, REVEAL_GROUP, RevealSlot, StatusDot, TONE_CLASS, Tooltip, cn, formatBytes, plural, tableClass, toast } from '@orgus/ui';
import { InlineText } from '../detail/InlineEdit.tsx';
import { setNode } from './data.ts';
import { NodeLabels, toggleDrain } from './NodeControls.tsx';
import { nodeDetail, nodeStatus, wirePerMinute } from './model.ts';

/**
 * A slot bar: one slim segment per slot. Running sessions in the working tone, starts not
 * reported yet faint, free slots on the track; only the part past the limit is danger.
 */
function SlotBar({ c }: { c: NodeCapacity }) {
  // Running is running: full, drained or outside hours, the sessions in use wear the legend's
  // working fill (an offline machine has no bar at all). Only the part past the limit is danger.
  const fill = TONE_CLASS.working.bg;
  const cells = Math.max(c.limit, c.used);
  if (cells > 24) {
    const over = c.used > c.limit;
    return <MeterBar value={cells ? c.used / cells : 0} tone={over ? 'danger' : 'working'} label={`${c.used} of ${c.limit} slots used`} className="h-1 w-full" />;
  }
  return (
    <div className="flex h-1 w-full gap-1" aria-hidden>
      {Array.from({ length: Math.max(1, cells) }, (_, i) => {
        const used = i < c.used;
        const reserved = used && i >= c.used - c.reserved;
        const over = i >= c.limit;
        return (
          <span
            key={i}
            className={cn(
              'h-full flex-1 rounded-pill transition-colors duration-(--duration-fast)',
              !used && 'bg-active',
              used && !reserved && (over ? TONE_CLASS.failed.bg : fill),
              reserved && cn(TONE_CLASS.queued.bg, 'opacity-60'),
            )}
          />
        );
      })}
    </div>
  );
}

/**
 * Heavy-work room on a machine that reports its load: the work budget's slots in use (waiting
 * commands named after them), and the sessions beside it, quietly.
 */
function WorkBar({ c }: { c: NodeCapacity }) {
  const w = c.admission!.work!;
  const full = c.admission!.state === 'full';
  const sessions = `${plural(c.used, 'session')}${c.reserved ? `, ${c.reserved} starting` : ''}`;
  return (
    <>
      {/* Nothing in use: no empty bar, the line below says it. */}
      {/* Heavy work in use wears the legend's working fill, full or not; past the budget it is danger. */}
      {w.used > 0 && <MeterBar
        value={w.budget ? Math.min(1, w.used / w.budget) : 0}
        tone={w.used > w.budget ? 'danger' : 'working'}
        label={`${w.used} of ${w.budget} heavy-work slots in use${w.waiting ? `, ${w.waiting} waiting` : ''}`}
        className="h-1 w-full"
      />}
      {/* Slots, then sessions: the pair wraps as whole facts, never cut mid-word. */}
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 text-caption text-fg-subtle">
        <span className={cn('whitespace-nowrap num', full && 'text-fg-muted')}>
          {w.used} of {w.budget} slots{w.waiting ? `, ${w.waiting} waiting` : ''}
        </span>
        <span className="whitespace-nowrap num">{sessions}</span>
      </span>
    </>
  );
}

/** The machine's session limit: its value at rest, a box on click; empty is automatic. */
function LimitField({ machine: m, state, measured }: { machine: Machine; state: SchedulerState; measured: boolean }) {
  const own = state.nodes[m.id]?.maxSessions ?? null;
  const auto = sessionCap(m, null, measured);
  const fallback = auto.limit;
  const why = auto.from === 'memory' ? 'from its memory' : 'CPU count / 2';

  const commit = async (t: string) => {
    const next = t === '' ? null : Number(t);
    if (next === own) return;
    try {
      await setNode(m.id, { maxSessions: next });
      toast('Limit saved', {
        description: next == null ? `${m.name} runs up to ${fallback} at once (${why}).` : next === 0 ? `${m.name} takes no new sessions.` : `${m.name} runs up to ${plural(next, 'session')} at once.`,
        action: { label: 'Undo', onClick: () => void setNode(m.id, { maxSessions: own }).catch((e) => toast.error(e)) },
      });
    } catch (e) {
      toast.error(e);
      throw e;
    }
  };

  // The number lines up with the others on its right edge; "Auto" sits under an automatic one.
  return (
    <span className="flex min-w-0 flex-col lg:items-end">
      <span className="-ml-2 flex lg:-mr-2 lg:ml-0">
        <InlineText
          value={own == null ? '' : String(own)}
          empty={String(fallback)}
          label={`Session limit for ${m.name}${own == null ? ', automatic' : ''}`}
          hint={`Empty for automatic: ${why}, ${fallback}`}
          numeric
          align="end"
          width="w-20"
          validate={(t) => (t === '' || (Number.isInteger(Number(t)) && Number(t) >= 0 && Number(t) <= 1000) ? null : 'A whole number from 0 to 1000, or empty')}
          onCommit={commit}
        />
      </span>
      {own == null && (
        <span className="text-caption text-fg-subtle" aria-hidden>
          Auto
        </span>
      )}
    </span>
  );
}

/** Who uses the machine: at most two lines, project and count, then who; the rest behind "+N more". */
function UsageList({ c, projectName }: { c: NodeCapacity; projectName: (id: Id | null) => string }) {
  if (!c.usage.length) return <span className="text-caption text-fg-subtle">Nobody</span>;
  const label = (u: NodeCapacity['usage'][number]) => `${projectName(u.projectId)}: ${plural(u.count, 'session')}, ${u.person ? `for ${u.person}` : 'not started by a mission'}`;
  const shown = c.usage.slice(0, 2);
  const rest = c.usage.slice(2);
  return (
    <ul className="flex min-w-0 flex-col">
      {shown.map((u, i) => (
        <li key={`${u.person}:${u.projectId}`} className="flex min-w-0 items-baseline gap-2 whitespace-nowrap" aria-label={label(u)}>
          <span className="truncate text-fg-muted">{projectName(u.projectId)}</span>
          <span className="text-fg-muted num">{u.count}</span>
          <span className="truncate text-caption text-fg-subtle">{u.person ? `for ${u.person}` : 'no mission'}</span>
          {i === shown.length - 1 && rest.length > 0 && (
            <Tooltip content={rest.map(label).join('; ')}>
              <span tabIndex={0} className="shrink-0 rounded-control text-caption text-fg-subtle num" aria-label={`${rest.length} more: ${rest.map(label).join('; ')}`}>
                +{rest.length} more
              </span>
            </Tooltip>
          )}
        </li>
      ))}
    </ul>
  );
}

/** A cell that spans the row on a phone (two columns: labels and the limit share the last line). */
const WIDE = 'col-span-2 lg:col-span-1';
/** The columns from lg up: the machine's status line gets the width the Room column does not use. */
const COLS = 'lg:grid-cols-[minmax(200px,1.5fr)_minmax(240px,1.3fr)_minmax(200px,1.3fr)_minmax(140px,1fr)_auto_auto]';

/** A cell's tiny label on a phone, where the column headers are hidden. */
function PhoneLabel({ children }: { children: string }) {
  return (
    <span className="text-caption text-fg-subtle lg:hidden" aria-hidden>
      {children}
    </span>
  );
}

export function CapacityTable({
  capacity,
  machines,
  projects,
  state,
  now,
}: {
  capacity: NodeCapacity[];
  machines: Record<Id, Machine>;
  projects: Record<Id, Project>;
  state: SchedulerState;
  now: number;
}) {
  const navigate = useNavigate();
  const projectName = (id: Id | null) => (id ? projects[id]?.name ?? 'Unknown project' : 'No project');
  if (!capacity.length) {
    return (
      <EmptyState
        icon={<Server />}
        title="No machines yet"
        description="Connect a machine to see its room and who uses it"
        action={
          <Button variant="primary" onClick={() => navigate('/machines')}>
            Open Machines
          </Button>
        }
      />
    );
  }
  return (
    <div role="table" aria-label="Machines and who uses them" className="flex flex-col">
      <div role="row" className={cn(tableClass.head, COLS, 'hidden items-center gap-4 border-b border-border-subtle px-4 lg:grid')}>
        <span role="columnheader">Machine</span>
        <span role="columnheader">Room</span>
        <span role="columnheader">Used by</span>
        <span role="columnheader">Labels</span>
        {/* The last two columns are auto in every row's own grid: one fixed width keeps them aligned. */}
        <span role="columnheader" className="w-20 text-right">
          Limit
        </span>
        <span role="columnheader" className="w-24">
          <span className="sr-only">Actions</span>
        </span>
      </div>
      {capacity.map((c) => {
        const m = machines[c.machineId];
        if (!m) return null;
        const st = nodeStatus(c);
        const drained = !!c.drained;
        return (
          <div
            role="row"
            key={c.machineId}
            className={cn(REVEAL_GROUP, tableClass.row, COLS, 'relative grid h-auto min-h-row-2line grid-cols-2 items-start gap-x-4 gap-y-3 px-4 py-3 lg:items-center lg:gap-y-2 lg:py-2')}
          >
            <div role="cell" className={cn(WIDE, 'flex min-w-0 flex-col pr-16 lg:pr-0')}>
              <span className="flex min-w-0 items-center gap-2">
                <StatusDot tone={st.tone} label="" />
                <Link to={`/machines?machine=${encodeURIComponent(m.id)}`} className="truncate font-medium text-fg hover:underline">
                  {m.name}
                </Link>
              </span>
              {/* A phone card has the width to say it in full; the table row truncates (full text on hover). */}
              <span className="text-caption text-fg-subtle lg:truncate" title={nodeDetail(c, now) ?? undefined}>
                {st.label}
                {nodeDetail(c, now) ? `, ${nodeDetail(c, now)}` : ''}
                {/* ADR 0104: what this node sends the hub, from its own counters (the second meta fact). */}
                {wirePerMinute(m, now) !== null ? (
                  <>
                    , <span className="num">{formatBytes(wirePerMinute(m, now)!, 1)}</span>/min to the hub
                  </>
                ) : null}
              </span>
            </div>
            <div role="cell" className={cn(WIDE, 'flex min-w-0 flex-col gap-2')}>
              <PhoneLabel>Room</PhoneLabel>
              {!c.online ? (
                // Offline: its slots are not counted, so no empty bar that reads as broken.
                <span className="text-caption text-fg-subtle num">Not counted, {plural(c.limit, 'slot')} when online</span>
              ) : c.admission?.work ? (
                <WorkBar c={c} />
              ) : (
                <>
                  {c.used > 0 && <SlotBar c={c} />}
                  <span className="text-caption text-fg-subtle num" role="img" aria-label={`${c.used} of ${c.limit} sessions${c.reserved ? `, ${c.reserved} starting` : ''}${c.used > c.limit ? `, ${c.used - c.limit} over its limit` : ''}; no load report from this machine`}>
                    {c.used} of {c.limit} sessions
                    {c.reserved ? `, ${c.reserved} starting` : null}
                    {c.used > c.limit ? <span className="text-failed">, {c.used - c.limit} over</span> : null}
                  </span>
                </>
              )}
            </div>
            <div role="cell" className={cn(WIDE, 'flex min-w-0 flex-col gap-1 lg:gap-0')}>
              <PhoneLabel>Used by</PhoneLabel>
              <UsageList c={c} projectName={projectName} />
            </div>
            <div role="cell" className="flex min-w-0 flex-col gap-1 lg:gap-0">
              <PhoneLabel>Labels</PhoneLabel>
              <NodeLabels machine={m} align="start" max={2} />
            </div>
            <div role="cell" className="flex min-w-0 flex-col gap-1 lg:w-20 lg:gap-0">
              <PhoneLabel>Limit</PhoneLabel>
              <LimitField machine={m} state={state} measured={c.admission?.basis === 'measured'} />
            </div>
            {/* On a phone the action sits at the card's top right, beside the machine's name. */}
            <RevealSlot role="cell" className="absolute top-2 right-2 flex justify-end lg:static lg:w-24">
              <Tooltip content={drained ? 'Take new work again' : 'No new work starts; running sessions finish'}>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={drained ? <CirclePlay /> : <CirclePause />}
                  onClick={() => void toggleDrain(m, drained, c.used - c.reserved)}
                  aria-label={drained ? `Enable ${m.name}` : `Disable new work on ${m.name}`}
                >
                  {drained ? 'Enable' : 'Disable new work'}
                </Button>
              </Tooltip>
            </RevealSlot>
          </div>
        );
      })}
    </div>
  );
}

/** Small legend under the table: the colours the bars use. */
const LEGEND: readonly { label: string; swatch: string }[] = [
  { label: 'Heavy work or session', swatch: TONE_CLASS.working.bg },
  // Only the slots past a machine's limit are red; a full machine is still running, not a warning.
  { label: 'Over its limit', swatch: TONE_CLASS.failed.bg },
  { label: 'Starting', swatch: cn(TONE_CLASS.queued.bg, 'opacity-60') },
  { label: 'Free', swatch: 'bg-active' },
];

export function SlotLegend() {
  return (
    <div className="flex flex-wrap items-center gap-4 px-4 py-2 text-caption text-fg-subtle">
      {LEGEND.map((l) => (
        <span key={l.label} className="flex items-center gap-2">
          <span className={cn('size-2 rounded-pill', l.swatch)} /> {l.label}
        </span>
      ))}
    </div>
  );
}
